import xlsx from 'xlsx';
import ApiService from '@/core/services/api.service';
import { handleError, makeToastSuccess, makeToastFaile } from '@/utils/common';

/**
 * Mixin tái sử dụng cho tính năng import Excel
 * 1. Implement các method cần thiết:
 *    - mapExcelDataToItems(excelData) - map dữ liệu từ Excel sang format cần thiết
 *    - getValidationEndpoint() - trả về endpoint API để validate
 *    - getSubmitEndpoint() - trả về endpoint API để submit
 *    - getSubmitPayload() - trả về payload để submit (optional, default: { items: this.importItems })
 * 2. Gọi this.validateImportData() sau khi parse Excel
 * 3. Gọi this.submitImportData() để submit
 */
export default {
  data() {
    return {
      file: null,
      excellist: [],
      importItems: [],
      uploading: false,
      progress: 0,
      currentPage: 1,
      maxFileSize: 10 * 1024 * 1024, // 10MB default
      maxRows: 1000, // Max rows default
    };
  },
  methods: {
    /**
     * Validate file format (.xls, .xlsx)
     */
    validateFileFormat(file) {
      if (!file) {
        return false;
      }
      if (!/\.(xls|xlsx)$/.test(file.name.toLowerCase())) {
        return false;
      }
      return true;
    },

    /**
     * Validate file size
     */
    validateFileSize(file) {
      if (!file) {
        return { valid: false, message: 'File không tồn tại' };
      }
      if (file.size > this.maxFileSize) {
        const maxSizeMB = (this.maxFileSize / 1024 / 1024).toFixed(2);
        return {
          valid: false,
          message: `File quá lớn. Kích thước tối đa: ${maxSizeMB}MB`,
        };
      }
      return { valid: true };
    },

    /**
     * Parse Excel file và trả về JSON data
     */
    async parseExcelFile(file) {
      return new Promise((resolve, reject) => {
        const fileReader = new FileReader();

        fileReader.onprogress = (ev) => {
          if (ev.lengthComputable) {
            // Update progress: 0-50% for reading file
            this.progress = Math.round((ev.loaded / ev.total) * 50);
          }
        };

        fileReader.onload = (ev) => {
          try {
            this.progress = 60; // Reading done
            const data = ev.target.result;
            const XLSX = xlsx;
            const workbook = XLSX.read(data, {
              type: 'binary',
            });
            this.progress = 80; // Parsing done

            const wsname = workbook.SheetNames[0];
            const ws = XLSX.utils.sheet_to_json(workbook.Sheets[wsname], {
              blankRows: false,
            });

            this.progress = 90; // JSON conversion done
            resolve(ws);
          } catch (error) {
            reject(error);
          }
        };
        fileReader.onerror = () => {
          reject(new Error('Lỗi khi đọc file'));
        };
        fileReader.readAsBinaryString(file);
      });
    },

    /**
     * Handle file selection event
     * Component cần implement mapExcelDataToItems để map dữ liệu
     */
    async handleFileSelect(event) {
      this.uploading = true;
      this.importItems = [];
      this.progress = 0;
      const files = event.target.files[0];
      this.file = files;

      if (!files) {
        this.uploading = false;
        return;
      }

      // Validate file format
      if (!this.validateFileFormat(files)) {
        this.file = null;
        this.clearFiles();
        this.uploading = false;
        makeToastFaile(
          'Định dạng của file tải lên không chính xác. Vui lòng tải lên file có đuôi .xls hoặc .xlsx',
        );
        return;
      }

      // Validate file size
      const sizeCheck = this.validateFileSize(files);
      if (!sizeCheck.valid) {
        this.file = null;
        this.clearFiles();
        this.uploading = false;
        makeToastFaile(sizeCheck.message);
        return;
      }

      try {
        this.progress = 10;
        const excelData = await this.parseExcelFile(files);

        // Validate rows count
        if (excelData.length > this.maxRows) {
          makeToastFaile(
            `File chứa quá nhiều dòng (${excelData.length}). Tối đa: ${this.maxRows} dòng`,
          );
          this.clearFiles();
          this.uploading = false;
          return;
        }

        if (excelData.length === 0) {
          makeToastFaile('File không chứa dữ liệu');
          this.clearFiles();
          this.uploading = false;
          return;
        }

        this.excellist = excelData;
        this.progress = 95;

        // Component cần implement method này để map dữ liệu
        if (this.mapExcelDataToItems) {
          const mappedItems = this.mapExcelDataToItems(excelData);

          // Nếu có validation endpoint, gọi validate
          if (this.getValidationEndpoint) {
            await this.validateImportData(mappedItems);
          } else {
            // Nếu không có validation, set trực tiếp
            this.importItems = mappedItems;
            this.progress = 100;
            this.uploading = false;
          }
        } else {
          console.warn('mapExcelDataToItems method is not implemented');
          this.uploading = false;
        }
      } catch (error) {
        console.error('Error parsing Excel file:', error);
        handleError(error);
        this.clearFiles();
        this.uploading = false;
      }
    },

    /**
     * Validate import data qua API
     * Component cần implement getValidationEndpoint() để trả về endpoint
     */
    async validateImportData(importItems) {
      if (!importItems || importItems.length === 0) {
        this.uploading = false;
        return;
      }

      if (!this.getValidationEndpoint) {
        // Nếu không có validation endpoint, set trực tiếp
        this.importItems = importItems;
        this.uploading = false;
        return;
      }

      try {
        const endpoint = this.getValidationEndpoint();
        const payload = this.getValidationPayload
          ? this.getValidationPayload(importItems)
          : { items: importItems };

        const response = await ApiService.post(endpoint, payload);

        // Set validated data
        this.importItems = response.data.data || [];
      } catch (error) {
        handleError(error);
        this.importItems = [];
        this.clearFiles();
      } finally {
        this.uploading = false;
      }
    },

    /**
     * Submit import data
     * Component cần implement getSubmitEndpoint() và getSubmitPayload()
     */
    async submitImportData() {
      if (!this.importItems || this.importItems.length === 0) {
        return;
      }

      if (!this.getSubmitEndpoint) {
        console.warn('getSubmitEndpoint method is not implemented');
        return;
      }

      this.uploading = true;
      ApiService.setHeader();

      try {
        const endpoint = this.getSubmitEndpoint();
        const payload = this.getSubmitPayload
          ? this.getSubmitPayload()
          : { items: this.importItems };

        const response = await ApiService.post(endpoint, payload);
        const { message } = response.data;

        this.clearFiles();
        this.uploading = false;

        // Component có thể override onImportSuccess để handle success
        if (message) {
          makeToastSuccess(message);
        }

        // Component có thể override onImportSuccess để handle success
        if (this.onImportSuccess) {
          this.onImportSuccess(response);
        } else {
          // Default: redirect về list page
          if (this.getRedirectRoute) {
            this.$router.push({
              name: this.getRedirectRoute(),
            });
          }
        }
      } catch (error) {
        handleError(error);
        this.uploading = false;
      }
    },

    /**
     * Clear file input và reset state
     * Note: File input ref sẽ được clear bởi ExcelImport component khi cần
     */
    clearFiles() {
      this.file = null;
      this.excellist = [];
      this.importItems = [];
      this.progress = 0;
    },

    /**
     * Reset toàn bộ state về initial
     */
    resetImportState() {
      this.file = null;
      this.excellist = [];
      this.importItems = [];
      this.uploading = false;
      this.progress = 0;
      this.currentPage = 1;
      this.clearFiles();
    },
  },
  computed: {
    /**
     * Computed properties helpers
     */
    importItemLength() {
      return this.importItems.length;
    },
    validCount() {
      return this.importItems.filter((item) => item.isValid).length;
    },
    invalidCount() {
      return this.importItems.filter((item) => !item.isValid).length;
    },
    hasValidationErrors() {
      return this.importItems.some(
        (item) => item.errors && item.errors.length > 0,
      );
    },
  },
  beforeDestroy() {
    this.resetImportState();
  },
};
